import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Badge, Empty, Modal, Field } from "@/components/ui-kit";
import { fmtDate } from "@/lib/utils";
import { UserCog, Trash2, Ban, CheckCircle2, Search, Link2, SlidersHorizontal, KeyRound, Mail, Copy, Wand2, HeartHandshake, UserPlus, X } from "lucide-react";
import { toast } from "sonner";

const ROLE_COLORS = { admin: "red", coach: "blue", player: "primary", parent: "yellow" };
const ROLE_LABELS = { admin: "Administrateur", coach: "Coach", player: "Joueur", parent: "Parent" };
const ALL_ROLES = ["admin", "coach", "parent", "player"];
const STAFF_FUNCTIONS = [
  { key: "administrateur", label: "Administrateur", color: "red" },
  { key: "delegue", label: "Délégué", color: "purple" },
  { key: "coach", label: "Coach", color: "blue" },
  { key: "comite_parents", label: "Comité des parents", color: "yellow" },
  { key: "benevole", label: "Bénévole", color: "green" },
];
const FUNC_LABEL = Object.fromEntries(STAFF_FUNCTIONS.map((f) => [f.key, f.label]));
const FUNC_COLOR = Object.fromEntries(STAFF_FUNCTIONS.map((f) => [f.key, f.color]));

const isTestAccount = (email = "") =>
  /@(example\.com|resend\.dev|x\.com)$/i.test(email) ||
  /^(test[_-]|testreg|testdup|codeuser)/i.test(email);

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [hideTest, setHideTest] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editRoles, setEditRoles] = useState([]);
  const [editFuncs, setEditFuncs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [tempPw, setTempPw] = useState("");
  const [genPw, setGenPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [linkUser, setLinkUser] = useState(null);
  const [members, setMembers] = useState([]);
  const [memberQ, setMemberQ] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => toast.error("Erreur de chargement")).finally(() => setLoading(false));
    api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const refreshUsers = async () => { const r = await api.get("/admin/users"); setUsers(r.data); return r.data; };
  const openLink = (u) => { setLinkUser(u); setMemberQ(""); };
  const linkMember = async (mid) => {
    setLinkBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${linkUser.id}/link-member`, { member_id: mid });
      toast.success(data.role_added ? "Affilié lié · fonction Parent ajoutée au compte" : "Affilié lié");
      const list = await refreshUsers();
      setLinkUser(list.find((x) => x.id === linkUser.id) || null);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setLinkBusy(false); }
  };
  const unlinkMember = async (mid) => {
    try {
      await api.delete(`/members/${mid}/parents/${linkUser.id}`);
      toast.success("Affilié délié");
      const list = await refreshUsers();
      setLinkUser(list.find((x) => x.id === linkUser.id) || null);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const toggleStatus = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}/status`, { disabled: !u.disabled });
      toast.success(u.disabled ? "Compte réactivé" : "Compte désactivé");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${u.name || u.email} ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Compte supprimé");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const openEdit = (u) => { setEditing(u); setEditRoles(u.roles?.length ? u.roles : [u.role]); setEditFuncs(u.functions || []); };
  const toggleEditRole = (r) => setEditRoles((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
  const toggleEditFunc = (f) => setEditFuncs((prev) => prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]);
  const saveRoles = async () => {
    if (!editRoles.length) { toast.error("Sélectionnez au moins un accès."); return; }
    setSaving(true);
    try {
      await api.patch(`/admin/users/${editing.id}/roles`, { roles: editRoles });
      await api.patch(`/admin/users/${editing.id}/functions`, { functions: editFuncs });
      toast.success("Fonctions mises à jour");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const openPw = (u) => { setPwUser(u); setTempPw(""); setGenPw(""); };
  const sendReset = async () => {
    setPwBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${pwUser.id}/send-reset`);
      if (data.sent) toast.success(`Email de réinitialisation envoyé à ${data.email}`);
      else toast.warning("Code généré, mais l'email n'a pas pu être envoyé (service email à configurer / domaine à vérifier).");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setPwBusy(false); }
  };
  const applyTempPw = async () => {
    setPwBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${pwUser.id}/set-password`, { password: tempPw || undefined });
      setGenPw(data.password);
      toast.success("Mot de passe temporaire défini");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setPwBusy(false); }
  };
  const copyPw = () => { navigator.clipboard?.writeText(genPw); toast.success("Copié"); };

  const toggleCommittee = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}/committee`, { committee: !u.committee });
      toast.success(u.committee ? "Retiré du comité des parents" : "Ajouté au comité des parents");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const roles = Array.from(new Set(users.flatMap((u) => u.roles?.length ? u.roles : [u.role])));
  const filtered = users.filter((u) => {
    const uRoles = u.roles?.length ? u.roles : [u.role];
    const matchQ = !q || `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase());
    const matchRole = !roleFilter || uRoles.includes(roleFilter);
    const matchTest = !hideTest || !isTestAccount(u.email);
    return matchQ && matchRole && matchTest;
  });
  const testCount = users.filter((u) => isTestAccount(u.email)).length;

  return (
    <div data-testid="users-page">
      <PageHeader title="Utilisateurs" subtitle="Comptes ayant un accès à l'application (identifiant + mot de passe)." />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10" />
          <input data-testid="users-search" placeholder="Rechercher par nom ou email..."
            value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full h-11 pl-9 pr-4 rounded-lg bg-secondary border border-border text-foreground text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="users-filter-all" onClick={() => setRoleFilter("")}
            className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${!roleFilter ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            Tous
          </button>
          {roles.map((r) => (
            <button key={r} data-testid={`users-filter-${r}`} onClick={() => setRoleFilter(r)}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${roleFilter === r ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {ROLE_LABELS[r] || r}
            </button>
          ))}
          {testCount > 0 && (
            <button data-testid="toggle-hide-test" onClick={() => setHideTest((v) => !v)}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${hideTest ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}
              title="Masquer les comptes créés pour les tests (@example.com, test_, ...)">
              <span className={`w-2 h-2 rounded-full ${hideTest ? "bg-primary-foreground" : "bg-muted-foreground"}`} />
              {hideTest ? `Comptes de test masqués (${testCount})` : `Afficher tout (${testCount} de test)`}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <Empty text="Chargement..." />
      ) : filtered.length ? (
        <div className="space-y-2">
          {filtered.map((u) => (
            <Card key={u.id} data-testid="user-row" className={`py-4 ${u.disabled ? "opacity-60" : ""}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold truncate">{u.name || "—"}</span>
                    {(u.roles?.length ? u.roles : [u.role]).map((r) => (
                      <Badge key={r} color={ROLE_COLORS[r] || "gray"}>{ROLE_LABELS[r] || r}</Badge>
                    ))}
                    {u.disabled && <Badge color="red">Désactivé</Badge>}
                    {u.committee && <Badge color="green"><span className="flex items-center gap-1"><HeartHandshake size={12} /> Comité</span></Badge>}
                    {u.is_self && <Badge color="gray">Vous</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{u.email}</p>
                  {u.functions?.length > 0 && (
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid={`user-functions-${u.id}`}>
                      {u.functions.map((f) => <Badge key={f} color={FUNC_COLOR[f] || "gray"}>{FUNC_LABEL[f] || f}</Badge>)}
                    </div>
                  )}
                  {u.created_at && <p className="text-xs text-muted-foreground mt-0.5">Inscrit le {fmtDate(u.created_at)}</p>}
                  {u.linked_members?.length > 0 && (
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground flex-wrap">
                      <Link2 size={13} className="text-primary" />
                      <span className="font-semibold text-foreground">Affiliés liés :</span>
                      {u.linked_members.map((m, i) => (
                        <span key={i} className="px-2 py-0.5 rounded bg-secondary">{m.name || m}</span>
                      ))}
                    </div>
                  )}
                </div>
                {!u.is_self && (
                  <div className="flex items-center gap-2 shrink-0">
                    <Button data-testid="edit-user-roles-btn" variant="outline" onClick={() => openEdit(u)}>
                      <SlidersHorizontal size={15} /> Fonctions
                    </Button>
                    <Button data-testid={`link-member-btn-${u.id}`} variant="outline" onClick={() => openLink(u)}>
                      <UserPlus size={15} /> Affiliés{u.linked_members?.length ? ` (${u.linked_members.length})` : ""}
                    </Button>
                    <Button data-testid="user-password-btn" variant="outline" onClick={() => openPw(u)}>
                      <KeyRound size={15} /> Mot de passe
                    </Button>
                    <Button data-testid="toggle-committee-btn" variant={u.committee ? "ghost" : "outline"} onClick={() => toggleCommittee(u)}>
                      <HeartHandshake size={15} /> {u.committee ? "Retirer du comité" : "Ajouter au comité"}
                    </Button>
                    <Button data-testid="toggle-user-status-btn" variant={u.disabled ? "primary" : "ghost"} onClick={() => toggleStatus(u)}>
                      {u.disabled ? <><CheckCircle2 size={15} /> Activer</> : <><Ban size={15} /> Désactiver</>}
                    </Button>
                    <button data-testid="delete-user-btn" onClick={() => remove(u)}
                      className="p-2 text-muted-foreground hover:text-destructive transition-colors" title="Supprimer le compte">
                      <Trash2 size={17} />
                    </button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : <Empty text="Aucun utilisateur trouvé." />}

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Modifier les fonctions">
        {editing && (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              <b className="text-foreground">{editing.name || editing.email}</b> — un compte peut cumuler plusieurs fonctions. L'accès correspond à l'ensemble des droits des fonctions cochées.
            </p>
            <Field label="Accès du compte">
              <div className="space-y-2">
                {ALL_ROLES.map((r) => (
                  <label key={r} data-testid={`role-option-${r}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors ${editRoles.includes(r) ? "border-primary bg-primary/10" : "border-border hover:bg-secondary"}`}>
                    <input type="checkbox" checked={editRoles.includes(r)} onChange={() => toggleEditRole(r)}
                      className="w-4 h-4 accent-primary" />
                    <span className="font-semibold text-sm">{ROLE_LABELS[r]}</span>
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Fonctions au club (annuaire staff)">
              <p className="text-xs text-muted-foreground mb-2">Ces fonctions apparaissent dans l'annuaire Staff / Comité. Elles se gèrent ici, sur les comptes utilisateurs.</p>
              <div className="flex flex-wrap gap-2" data-testid="user-functions-select">
                {STAFF_FUNCTIONS.map((f) => {
                  const active = editFuncs.includes(f.key);
                  return (
                    <button key={f.key} type="button" data-testid={`func-option-${f.key}`} onClick={() => toggleEditFunc(f.key)}
                      className={`px-3 py-2 rounded-full text-xs font-bold transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Button data-testid="save-user-roles-btn" onClick={saveRoles} disabled={saving} className="w-full mt-2">
              {saving ? "Enregistrement..." : "Enregistrer les fonctions"}
            </Button>
          </>
        )}
      </Modal>

      <Modal open={!!pwUser} onClose={() => setPwUser(null)} title="Gérer le mot de passe">
        {pwUser && (
          <div className="space-y-5">
            <p className="text-sm text-muted-foreground">
              Compte : <b className="text-foreground">{pwUser.name || pwUser.email}</b>{pwUser.email ? ` · ${pwUser.email}` : ""}
            </p>

            <div className="rounded-lg border border-border p-4">
              <p className="font-semibold text-sm flex items-center gap-2 mb-1"><Mail size={15} className="text-primary" /> Envoyer un email de réinitialisation</p>
              <p className="text-xs text-muted-foreground mb-3">L'utilisateur reçoit un code par email et choisit lui-même son nouveau mot de passe.</p>
              <Button data-testid="send-reset-email-btn" variant="outline" onClick={sendReset} disabled={pwBusy || !pwUser.email} className="w-full">
                <Mail size={15} /> Envoyer le code par email
              </Button>
            </div>

            <div className="rounded-lg border border-border p-4">
              <p className="font-semibold text-sm flex items-center gap-2 mb-1"><KeyRound size={15} className="text-primary" /> Définir un mot de passe temporaire</p>
              <p className="text-xs text-muted-foreground mb-3">À communiquer directement à l'utilisateur. Fonctionne sans email.</p>
              <div className="flex gap-2">
                <input data-testid="temp-password-input" value={tempPw} onChange={(e) => setTempPw(e.target.value)}
                  placeholder="Laisser vide pour générer" className="flex-1 rounded-lg bg-secondary border border-border p-2.5 text-sm outline-none focus:border-primary" />
                <Button data-testid="apply-temp-password-btn" onClick={applyTempPw} disabled={pwBusy}>
                  <Wand2 size={15} /> Appliquer
                </Button>
              </div>
              {genPw && (
                <div data-testid="temp-password-result" className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-primary/10 border border-primary/40 px-3 py-2">
                  <span className="font-mono font-bold text-primary text-sm break-all">{genPw}</span>
                  <button data-testid="copy-temp-password-btn" onClick={copyPw} className="text-muted-foreground hover:text-primary shrink-0" title="Copier">
                    <Copy size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!linkUser} onClose={() => setLinkUser(null)} title="Affiliés liés au compte">
        {linkUser && (() => {
          const linkedIds = (linkUser.linked_members || []).map((m) => m.id).filter(Boolean);
          const available = members
            .filter((m) => !linkedIds.includes(m.id))
            .filter((m) => { const t = `${m.first_name} ${m.last_name} ${m.category || ""}`.toLowerCase(); return !memberQ || t.includes(memberQ.toLowerCase()); })
            .sort((a, b) => `${a.last_name}`.localeCompare(`${b.last_name}`));
          return (
            <div className="space-y-5">
              <p className="text-sm text-muted-foreground">
                Compte : <b className="text-foreground">{linkUser.name || linkUser.email}</b>. Liez un ou plusieurs affiliés (enfants) ; le parent verra alors leurs informations et tableau de bord.
              </p>

              <div data-testid="linked-members-list">
                <p className="font-semibold text-sm mb-2">Affiliés actuellement liés</p>
                {(linkUser.linked_members || []).length ? (
                  <div className="flex flex-wrap gap-2">
                    {linkUser.linked_members.map((m) => (
                      <span key={m.id} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-primary/15 text-primary text-sm font-semibold">
                        {m.name}
                        <button data-testid={`unlink-member-${m.id}`} onClick={() => unlinkMember(m.id)}
                          className="hover:text-destructive" title="Délier">
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">Aucun affilié lié pour le moment.</p>}
              </div>

              <div>
                <p className="font-semibold text-sm mb-2">Ajouter un affilié</p>
                <div className="relative mb-2">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10" />
                  <input data-testid="link-member-search" placeholder="Rechercher un affilié..."
                    value={memberQ} onChange={(e) => setMemberQ(e.target.value)}
                    className="w-full h-11 pl-9 pr-4 rounded-lg bg-secondary border border-border text-foreground text-base sm:text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div className="space-y-1.5 max-h-[40vh] overflow-y-auto pr-1">
                  {available.length ? available.map((m) => (
                    <div key={m.id} data-testid={`link-candidate-${m.id}`} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-secondary/40">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{m.first_name} {m.last_name}</p>
                        {m.category && <p className="text-xs text-muted-foreground">{m.category}</p>}
                      </div>
                      <Button data-testid={`link-add-${m.id}`} disabled={linkBusy} onClick={() => linkMember(m.id)} className="shrink-0">
                        <UserPlus size={15} /> Lier
                      </Button>
                    </div>
                  )) : <Empty text="Aucun affilié disponible." />}
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
